import Track from './Track';
import Alert from './Alert';
import Region from './Region';
import Place from './Place';
import PlaceType from './PlaceType';
import axios from 'axios';
import Area from './Area';
import { DataOrigin, type LayersDictionary } from './types';

import { createStore, Store } from 'vuex';
import { type ControlSaveTiles } from '@/ts/leafletoffline';

const speedScales = {1: {'thresholds': [0, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22], 'colors': [{color: 'rgb(229,11,10)'}, {color: 'rgb(231,106,12)'}, {color: 'rgb(231,154,12)'}, {color: 'rgb(228,201,12)'}, {color: 'rgb(210,230,12)'}, {color: 'rgb(164,231,12)'}, {color: 'rgb(68,231,12)'}, {color: 'rgb(11,230,143)'}, {color: 'rgb(12,220,230)'}, {color: 'rgb(12,175,230)'}, {color: 'rgb(12,126,230)'}, {color: 'rgb(11,77,229)'}, {color: 'rgb(88,12,230)'}, {color: 'rgb(137,12,230)'}, {color: 'rgb(181,11,229)'}, {color: 'rgb(230,12,229)'}]},
                     2: {'thresholds': [0, 5, 10, 15, 20], 'colors': [{color: 'rgb(229,11,10)'}, {color: 'rgb(231,154,12)'}, {color: 'rgb(210,230,12)'}, {color: 'rgb(11,230,143)'}, {color: 'rgb(12,126,230)'}]}}

const translations: {[key: string]: {[key: string]: string}} = {'pl': {}, 'en': {}}

export interface State {
  alerts: Alert[];
  zoomLevel: number;
  tracks: Track[];
  imports: Track[];
  plannedTracks: Track[];
  trailTracks: Track[];
  otherPeopleTracks: Track[];
  eventTracks: Track[];
  borderTracks: Track[];
  tmpPlannedTracks: Track[];
  playingSpeed: number;
  regions: Region[];
  token: string;
  user: string;
  places: Place[];
  placeTypes: PlaceType[];
  
  speedLegendVisible: boolean;
  speedScales: typeof speedScales;
  speedThresholds: number[];
  speedColors: {'color': string}[];

  trackDetailsActivated: Track[];

  editedTrack: Track;
  editedArea: Area;

  baseMaps: LayersDictionary;

  areas: Area[];
  translations: typeof translations;
  dataRevision: {[key: string]: number};
  maplasPlaces: {[key: number]: Place[]};
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $store: Store<State>;
    $refs: {[key: string]: HTMLElement};
    $DataOrigin: typeof DataOrigin;
  }
}

const store = createStore<State>({
  state () {
    return {
      alerts: [],
      zoomLevel: null,
      tracks: Array<Track>(),
      imports: Array<Track>(),
      plannedTracks: Array<Track>(),
      trailTracks: Array<Track>(),
      otherPeopleTracks: Array<Track>(),
      eventTracks: Array<Track>(),
      borderTracks: Array<Track>(),
      tmpPlannedTracks: Array<Track>(),
      playingSpeed: 10,
      regions: Array<Region>(),
      token: '',
      user: null,
      places: Array<Place>(),
      placeTypes: Array<PlaceType>(),

      speedLegendVisible: false,
      speedScales: speedScales,
      speedThresholds: speedScales[1]['thresholds'],
      speedColors: speedScales[1]['colors'],

      trackDetailsActivated: [],

      editedTrack: null,
      editedArea: null,

      baseMaps: null,

      areas: Array<Area>(),
      translations: translations,
      dataRevision: null,
      maplasPlaces: {},
    }
  },
  getters: {
    selectedTracks: (state): Track[] => {
      const tracks = [];
      for (const track of state.tracks) {
        if (track.checked) {
          tracks.push(track);
        }
      }
      return tracks;
    },
  },
  mutations: {
    initialize(state) {
      // @ts-ignore
      for(const dataOrigin of Object.values(DataOrigin).filter(value => typeof value === 'number')) {
        state.maplasPlaces[dataOrigin] = [];
      }
    },
    setZoomLevel(state, zoomLevel: number) {
      state.zoomLevel = zoomLevel;
    },
    setToken(state, token: any) {
      state.token = token;
      if (token) {
        axios.defaults.headers.common['Authorization'] = 'Token ' + token;
        localStorage.setItem('maplasToken', token);
      } else {
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem('maplasToken');
      }
    },
    setUser(state, user: string) {
      state.user = user;
      if (user) {
        localStorage.setItem('maplasUser', user);
      } else {
        localStorage.removeItem('maplasUser');
      }
      const markersDraggable = Boolean(user);
      for (const place of state.places) {
        place.setDraggable(markersDraggable);
      }
    },
    setTracks(state, tracks: Track[]) {
      state.tracks = tracks;
    },
    setTmpPlannedTracks(state, tracks: Track[]) {
      state.tmpPlannedTracks = tracks;
    },
    setPlannedTracks(state, tracks: Track[]) {
      state.plannedTracks = tracks;
    },
    setEventTracks(state, tracks: Track[]) {
      state.eventTracks = tracks;
    },
    setBorderTracks(state, tracks: Track[]) {
      state.borderTracks = tracks;
    },
    setTrailTracks(state, tracks: Track[]) {
      state.trailTracks = tracks;
    },
    setOtherPeopleTracks(state, tracks: Track[]) {
      state.otherPeopleTracks = tracks;
    },
    setSpeedLegendVisible(state, speedLegendVisible: boolean) {
      state.speedLegendVisible = speedLegendVisible;
    },
    setSpeedScale(state, scale: 1 | 2) {
      if (scale in state.speedScales) {
        state.speedThresholds = state.speedScales[scale]['thresholds'];
        state.speedColors = state.speedScales[scale]['colors'];
      }
    },
    setRegions(state, regions: Region[]) {
      state.regions = regions;
    },
    setPlaces(state, places: Place[]) {
      state.places = places;
    },
    setPlaceTypes(state, placeTypes: PlaceType[]) {
      state.placeTypes = placeTypes;
    },
    addAlert(state, alert: Alert) {
      state.alerts.push(alert);
    },
    removeAlert(state, id: number) {
      for (const index in state.alerts) {
        if (state.alerts[index].id === id) {
          state.alerts.splice(Number(index), 1);
        }
      }
    },
    addImportedTrack(state, track) {
      state.imports.push(track);
      state.imports = state.imports.slice();
    },
    addPlannedTrack(state, track) {
      state.tmpPlannedTracks.push(track);
    },
    setEditedTrack(state, track) {
      state.editedTrack = track;
      state.editedArea = null;
    },
    setEditedArea(state, area) {
      state.editedArea = area;
      state.editedTrack = null;
    },
    addPlace(state, place) {
      let exists = false;
      let index = 0;
      for (const loopPlace of state.places) {
        if (loopPlace.id == place.id) {
          loopPlace.name = place.name;
          loopPlace.description = place.description;
          loopPlace.approved = place.approved;
          loopPlace.photos = place.photos;
          loopPlace.lat = place.lat;
          loopPlace.lon = place.lon;
          loopPlace.type = place.type;
          exists = true;
          break;
        }
        index = index + 1;
      }
      if (! exists) {
        state.places.push(place);
      }
    },
    removeImportedTrack(state, track) {
      const index = state.imports.indexOf(track);
      if (index >= 0) {
        state.imports = state.imports.toSpliced(index, 1);
      }
    },
    removePlannedTrack(state, track) {
      const index = state.tmpPlannedTracks.indexOf(track);
      if (index >= 0) {
        state.tmpPlannedTracks = state.tmpPlannedTracks.toSpliced(index, 1)
      }
    },
    addTrack(state, track: Track) {
      if (track.gpsTrack.isDoneTrack()) {
        state.tracks.push(track);
        state.tracks = state.tracks.slice();
      }
      if (track.gpsTrack.isPlannedTrack()) {
        state.plannedTracks.push(track);
        state.plannedTracks = state.plannedTracks.slice();
      }
      if (track.gpsTrack.isOtherPeopleTrack()) {
        state.otherPeopleTracks.push(track);
        state.otherPeopleTracks = state.otherPeopleTracks.slice();
      }
      if (track.gpsTrack.isEventTrack()) {
        state.eventTracks.push(track);
        state.eventTracks = state.eventTracks.slice();
      }
      if (track.gpsTrack.isTrailTrack()) {
        state.trailTracks.push(track);
        state.trailTracks = state.trailTracks.slice();
      }
      if (track.gpsTrack.isBorderTrack()) {
        state.borderTracks.push(track);
        state.borderTracks = state.borderTracks.slice();
      }
    },
    sortTracks(state) {
      state.tracks.sort((a, b) => a.gpsTrack.start_time < b.gpsTrack.start_time ? 1 : -1);
      state.tracks = state.tracks.slice();
    },
    setTrackChecked(state, options: {track: Track, checked: boolean}) {
      for (const looptrack of state.tracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.imports) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.tmpPlannedTracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.plannedTracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.trailTracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.eventTracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.otherPeopleTracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
      for (const looptrack of state.borderTracks) {
        if (looptrack === options.track) {
          looptrack.checked = options.checked;
        }
      }
    },
    addTranslation(state, payload: {lang: string, key: string, value: string}) {
      state.translations[payload.lang][payload.key] = payload.value;
    }
  },
  actions: {
  },
});

export default store;
